import { Component, computed, forwardRef, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { AddressItemDto } from '@artemis-software/wr-api';

@Component({
  selector: 'wr-building-billing-addresses',
  templateUrl: './building-billing-addresses.component.html',
  styleUrls: ['./building-billing-addresses.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuildingBillingAddressesComponent),
      multi: true,
    },
  ],
})
export class BuildingBillingAddressesComponent extends AbstractFormControl<AddressItemDto[]>{

  readonly formArray = signal<FormArray|undefined>(undefined);

  readonly controls = computed(() => {
    this.disabledSignal();
    return this.formArray()?.controls ?? [];
  });

  override writeValue(value: AddressItemDto[] | undefined) {
    super.writeValue(value);
    this.formArray.set(this.initForm(value));
  }

  initForm(value: AddressItemDto[] | undefined) {
    const addresses = value ?? [];

    const formArray = new FormArray(addresses.map((address) => {
      return new FormGroup({
        id: new FormControl(address.id),
        recipient: new FormControl(address.recipient),
        street: new FormControl(address.street, [Validators.required]),
        number: new FormControl(address.number, [Validators.required]),
        addressLine2: new FormControl(address.addressLine2),
        uid: new FormControl(address.uid),
        country: new FormControl(address.country ?? 'AT'),
        automaticAddress: new FormControl({
          zipCode: address.zipCode,
          city: address.city,
          state: address.state
        }
        ),
        markedForDeletion: new FormControl(false),
      });
    }));

    formArray.valueChanges.subscribe((newValue: any[]) => {
      this.value = newValue.map(item => ({
        ...item,
        zipCode: item.automaticAddress?.zipCode,
        city: item.automaticAddress?.city,
        state: item.automaticAddress?.state,
        markedForDeletion: item.markedForDeletion,
      }));
    });

    return formArray;
  }

  addBillingAddress() {
    this.formArray()?.push(new FormGroup({
      id: new FormControl(''),
      recipient: new FormControl(''),
      street: new FormControl('', [Validators.required]),
      number: new FormControl('', [Validators.required]),
      addressLine2: new FormControl(''),
      uid: new FormControl(''),
      country: new FormControl('AT'),
      automaticAddress: new FormControl({
        zipCode: '',
        city: '',
        state: ''
      }
      ),
      markedForDeletion: new FormControl(false),
    }));
  }

  deleteBillingAddress(index: number) {
    const id = this.formArray()?.at(index)?.get('id')?.value;

    if (!id) {
      this.formArray()?.removeAt(index);
      return;
    }

    const group = this.formArray()?.at(index) as FormGroup;
    group.get('markedForDeletion')?.setValue(true);
    group.get('street')?.clearValidators();
    group.get('number')?.clearValidators();
    group.get('street')?.updateValueAndValidity();
    group.get('number')?.updateValueAndValidity();
  }

  isDeleted(index: number): boolean {
    return this.formArray()?.at(index)?.get('markedForDeletion')?.value === true;
  }

  override setDisabledState(isDisabled: boolean) {
    super.setDisabledState(isDisabled);
    if(isDisabled){
      this.formArray()?.disable({ emitEvent: false });
    } else {
      this.formArray()?.enable({ emitEvent: false });
    }
  }

  hasError(index: number, controlName: string): boolean {
    const group = this.formArray()?.at(index) as FormGroup;
    if (group.get('markedForDeletion')?.value) return false;

    const control = group.get(controlName);
    return !!(control?.invalid && (control.dirty || control.touched));
  }
}
